// Tiny Web Audio chimes for Universe & Co. (/manifest). Everything is
// synthesized on the fly — no audio assets. All playback is triggered from
// click handlers, so creating the AudioContext satisfies autoplay policies.
// Every function degrades to a silent no-op when Web Audio is unavailable.

// Shared, lazily-created context (browsers cap how many can exist).
let ctx = null;
const getCtx = () => {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    if (!ctx) ctx = new AC();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
};

// The little pluck heard when a wish lands in the basket.
export const ADD_BLIP = {
  freq: 880, // A5, gliding up a fourth
  glideTo: 1174.66, // D6
  dur: 0.09,
  gain: 0.045,
};

// The ascending arpeggio heard as an order is released: C-major climbing to
// a high sparkle, each note ringing out like a struck bell.
export const CHIME_NOTES = [
  { freq: 523.25, at: 0 }, // C5
  { freq: 659.25, at: 0.16 }, // E5
  { freq: 783.99, at: 0.32 }, // G5
  { freq: 1046.5, at: 0.5 }, // C6
  { freq: 1567.98, at: 0.72 }, // G6
];

// One bell-like note: a sine with a quiet, slightly-detuned octave partner
// (the detune gives a gentle shimmer as they beat against each other).
const ringNote = (c, freq, at) => {
  const t = c.currentTime + at;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.06, t + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 2.2);
  gain.connect(c.destination);

  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, t);
  osc.connect(gain);

  const shimmer = c.createOscillator();
  shimmer.type = "sine";
  shimmer.frequency.setValueAtTime(freq * 2.003, t);
  const shimmerGain = c.createGain();
  shimmerGain.gain.setValueAtTime(0.22, t);
  shimmer.connect(shimmerGain);
  shimmerGain.connect(gain);

  osc.start(t);
  shimmer.start(t);
  osc.stop(t + 2.3);
  shimmer.stop(t + 2.3);
};

// A soft rising whoosh under the chime: filtered noise sweeping upward, like
// the parcel leaving the atmosphere.
const whoosh = (c) => {
  const dur = 2.2;
  const t = c.currentTime;
  const buffer = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;

  const src = c.createBufferSource();
  src.buffer = buffer;

  const band = c.createBiquadFilter();
  band.type = "bandpass";
  band.Q.value = 1.2;
  band.frequency.setValueAtTime(160, t);
  band.frequency.exponentialRampToValueAtTime(1800, t + dur * 0.8);

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.05, t + 0.35);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  src.connect(band);
  band.connect(gain);
  gain.connect(c.destination);
  src.start(t);
  src.stop(t + dur);
};

export const playAddBlip = () => {
  const c = getCtx();
  if (!c) return;
  try {
    const t = c.currentTime;
    const gain = c.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(ADD_BLIP.gain, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + ADD_BLIP.dur + 0.06);
    gain.connect(c.destination);

    const osc = c.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(ADD_BLIP.freq, t);
    osc.frequency.exponentialRampToValueAtTime(ADD_BLIP.glideTo, t + ADD_BLIP.dur);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + ADD_BLIP.dur + 0.1);
  } catch {
    /* stay silent */
  }
};

export const playReleaseChime = () => {
  const c = getCtx();
  if (!c) return;
  try {
    whoosh(c);
    CHIME_NOTES.forEach((note) => ringNote(c, note.freq, note.at));
  } catch {
    /* stay silent */
  }
};
